/**
 * Almacen local durable (spec/02-architecture.md §3, "Replica local").
 * Interfaz clave-valor por coleccion, con escritura por lotes atomica
 * (F2, spec/02-architecture.md §4: la transaccion local es la unidad de
 * durabilidad — o se escribe todo, o no se escribe nada).
 *
 * Dos implementaciones:
 *  - InMemoryLocalStore: para tests y como base de referencia del contrato.
 *  - IndexedDbLocalStore: real, para navegador. No se ejecuta en Node (no hay
 *    IndexedDB); se compila y se deja lista para que Antigravity la conecte
 *    al arranque real de la PWA. Su correctud estructural se verifica por
 *    tipos (tsc); su comportamiento en navegador es responsabilidad de las
 *    pruebas E2E de Antigravity (fuera del alcance de este nucleo).
 */

import { CURRENT_SCHEMA_VERSION, applyMigrations, planMigrations, type Migration } from "./schema.js";

export interface StoredItem<T> {
  readonly id: string;
  readonly value: T;
}

export interface LocalStore {
  get<T>(collection: string, id: string): Promise<T | undefined>;
  list<T>(collection: string): Promise<StoredItem<T>[]>;
  /** Escritura atomica: todos los items se aplican, o ninguno (durabilidad de F2). */
  putBatch<T>(collection: string, items: readonly StoredItem<T>[]): Promise<void>;
  delete(collection: string, id: string): Promise<void>;
  readMeta<T>(key: string): Promise<T | undefined>;
  writeMeta<T>(key: string, value: T): Promise<void>;
}

const SCHEMA_VERSION_META_KEY = "schema_version";

/**
 * Abre el almacen aplicando migraciones hacia adelante si hace falta.
 * Lanza UnknownFutureSchemaVersionError (via planMigrations) si la version
 * guardada es superior a la conocida — el arranque se detiene ahi, sin tocar
 * ningun dato.
 */
export async function openWithMigrations(
  store: LocalStore,
  migrations: readonly Migration[],
  migrateCollection: (
    store: LocalStore,
    apply: (data: unknown) => unknown,
  ) => Promise<void>,
): Promise<void> {
  const storedVersion = (await store.readMeta<number>(SCHEMA_VERSION_META_KEY)) ?? 0;
  const plan = planMigrations(storedVersion, migrations);
  if (plan.length > 0) {
    await migrateCollection(store, (data) => applyMigrations(data, plan));
  }
  await store.writeMeta(SCHEMA_VERSION_META_KEY, CURRENT_SCHEMA_VERSION);
}

/** Implementacion en memoria: base del contrato, usada en todos los tests del nucleo. */
export class InMemoryLocalStore implements LocalStore {
  private readonly collections = new Map<string, Map<string, unknown>>();
  private readonly meta = new Map<string, unknown>();

  private collection(name: string): Map<string, unknown> {
    let c = this.collections.get(name);
    if (!c) {
      c = new Map();
      this.collections.set(name, c);
    }
    return c;
  }

  async get<T>(collection: string, id: string): Promise<T | undefined> {
    return this.collection(collection).get(id) as T | undefined;
  }

  async list<T>(collection: string): Promise<StoredItem<T>[]> {
    return Array.from(this.collection(collection).entries()).map(([id, value]) => ({
      id,
      value: value as T,
    }));
  }

  async putBatch<T>(collection: string, items: readonly StoredItem<T>[]): Promise<void> {
    // Atomico por construccion: sincrono, sin punto de fallo intermedio observable.
    const c = this.collection(collection);
    for (const item of items) c.set(item.id, item.value);
  }

  async delete(collection: string, id: string): Promise<void> {
    this.collection(collection).delete(id);
  }

  async readMeta<T>(key: string): Promise<T | undefined> {
    return this.meta.get(key) as T | undefined;
  }

  async writeMeta<T>(key: string, value: T): Promise<void> {
    this.meta.set(key, value);
  }
}

/**
 * Implementacion real sobre IndexedDB nativo del navegador. Sin dependencias
 * externas. No se ejecuta en el entorno de tests de este nucleo (Node no
 * implementa IndexedDB); Antigravity la valida en E2E de navegador real.
 */
export class IndexedDbLocalStore implements LocalStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly dbName: string,
    private readonly knownCollections: readonly string[],
  ) {}

  private open(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const idb = (globalThis as { indexedDB?: IDBFactory }).indexedDB;
      if (!idb) {
        reject(new Error("IndexedDB no disponible en este entorno (uso de IndexedDbLocalStore fuera de un navegador)"));
        return;
      }
      const request = idb.open(this.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        for (const name of [...this.knownCollections, "__meta__"]) {
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Fallo al abrir IndexedDB"));
    });
    return this.dbPromise;
  }

  private async withStore<T>(name: string, mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> {
    const db = await this.open();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(name, mode);
      const store = tx.objectStore(name);
      const req = fn(store);
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => reject(req.error ?? new Error(`Fallo de IndexedDB en ${name}`));
    });
  }

  async get<T>(collection: string, id: string): Promise<T | undefined> {
    const value = await this.withStore<T | undefined>(collection, "readonly", (s) => s.get(id));
    return value;
  }

  async list<T>(collection: string): Promise<StoredItem<T>[]> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(collection, "readonly");
      const store = tx.objectStore(collection);
      const items: StoredItem<T>[] = [];
      const cursorReq = store.openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          items.push({ id: String(cursor.key), value: cursor.value as T });
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      cursorReq.onerror = () => reject(cursorReq.error ?? new Error("Fallo al listar IndexedDB"));
    });
  }

  async putBatch<T>(collection: string, items: readonly StoredItem<T>[]): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(collection, "readwrite");
      const store = tx.objectStore(collection);
      for (const item of items) store.put(item.value, item.id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("Fallo de transaccion atomica en IndexedDB"));
      tx.onabort = () => reject(tx.error ?? new Error("Transaccion de IndexedDB abortada"));
    });
  }

  async delete(collection: string, id: string): Promise<void> {
    await this.withStore(collection, "readwrite", (s) => s.delete(id));
  }

  async readMeta<T>(key: string): Promise<T | undefined> {
    return this.get<T>("__meta__", key);
  }

  async writeMeta<T>(key: string, value: T): Promise<void> {
    await this.putBatch("__meta__", [{ id: key, value }]);
  }
}
